<template>
  <section class="inputUserDataSection" :class="{ isPc: !isSp }">
    <TitleText id="titleArea" text="登録情報入力" :wide-sp="true" />
    <ServerErrorArea class="serverErrorArea" :error-type="errorType" :server-error-list="serverErrorList" />
    <InputName
      id="nameInputArea"
      :error-from-outer-sei="errors.name.sei"
      :error-from-outer-mei="errors.name.mei"
      :name-input-type="FULL_NAME_TYPE.KANJI"
      :name-object="inputs.name"
      :watch-name-object-flg="actionWatchNameObjectFlg"
      class="inputArea"
      :class="{ isPc: !isSp }"
      area-label="名前"
      required
      @change="onChangeGeneral('name', $event)"
      @watchValidationSei="watchGeneralNestedValidation('name', 'sei', $event)"
      @watchValidationMei="watchGeneralNestedValidation('name', 'mei', $event)"
      @focusSei="onFocusGeneralNested('name', 'sei')"
      @focusMei="onFocusGeneralNested('name', 'mei')"
      @blurSei="onBlurGeneralNested('name', 'sei')"
      @blurMei="onBlurGeneralNested('name', 'mei')"
    />
    <InputName
      id="hiraganaNameInputArea"
      :error-from-outer-sei="errors.hiraganaName.sei"
      :error-from-outer-mei="errors.hiraganaName.mei"
      :name-input-type="FULL_NAME_TYPE.HIRAGANA"
      :name-object="inputs.hiraganaName"
      :watch-name-object-flg="actionWatchNameKanaObjectFlg"
      class="inputArea"
      :class="{ isPc: !isSp }"
      area-label="ふりがな"
      required
      @change="onChangeGeneral('hiraganaName', $event)"
      @watchValidationSei="watchGeneralNestedValidation('hiraganaName', 'sei', $event)"
      @watchValidationMei="watchGeneralNestedValidation('hiraganaName', 'mei', $event)"
      @focusSei="onFocusGeneralNested('hiraganaName', 'sei')"
      @focusMei="onFocusGeneralNested('hiraganaName', 'mei')"
      @blurSei="onBlurGeneralNested('hiraganaName', 'sei')"
      @blurMei="onBlurGeneralNested('hiraganaName', 'mei')"
    />
    <SelectYmd
      id="birthdayInputArea"
      :error-from-outer="errors.birthday"
      class="inputArea"
      :class="{ isPc: !isSp }"
      area-label="生年月日"
      required
      @watchValidation="watchGeneralValidation('birthday', $event)"
      @change="onChangeGeneral('birthday', $event)"
    />
    <SelectGender
      id="genderInputArea"
      v-model="inputs.gender"
      :validate-at-mount="true"
      :gender-expression="inputs.genderExpression"
      :error-from-outer="errors.gender"
      :error-from-outer-expression="errors.genderExpression"
      class="inputArea"
      :class="{ isPc: !isSp }"
      required
      @watchValidation="watchGeneralValidation('gender', $event)"
      @watchInputTextValidation="watchInputTextValidation"
      @inputText="inputText"
      @changeInputValue="changeInputValue"
      @focusGender="focusGender"
    />
    <InputWithLabel
      id="telInputArea"
      v-model="inputs.tel"
      :error-from-outer="errors.tel"
      class="inputArea"
      :class="{ isPc: !isSp }"
      area-label="電話番号"
      input-type="text"
      placeholder="例）0312345678"
      required
      :validation-list="[VALIDATION_LIST.EMPTY, VALIDATION_LIST.TEL]"
      @watchValidation="watchGeneralValidation('tel', $event)"
      @focus="onFocusTel"
    />
    <PostalNumber
      v-if="'postal' in inputs"
      id="postalInputArea"
      v-model="inputs.postal"
      class="inputArea"
      :class="{ isPc: !isSp }"
      :error-from-outer="errors.postal"
      :postal-click-flg="postalClickFlg"
      required
      @onClick="onClickPostalNumberSuggest"
      @watchValidation="watchGeneralValidation('postal', $event)"
    />
    <SelectBoxArea
      v-if="'pref' in inputs"
      id="prefInputArea"
      class="inputArea"
      :class="{ isPc: !isSp }"
      area-label="都道府県"
      :select-list="TODOFUKEN_LIST"
      :input-value="inputs.pref"
      placeholder="都道府県を選択"
      required
      :error-from-outer="errors.pref"
      @change="onChangePref"
      @watchValidation="watchGeneralValidation('pref', $event)"
    />
    <InputWithLabel
      v-if="'address1' in inputs"
      id="address1InputArea"
      v-model="inputs.address1"
      :error-from-outer="errors.address1"
      class="inputArea"
      :class="{ isPc: !isSp }"
      area-label="市区町村"
      input-type="text"
      placeholder="例）千代田区丸の内"
      required
      :validation-list="[VALIDATION_LIST.EMPTY, VALIDATION_LIST.ADDRESS]"
      :max="36"
      @watchValidation="watchGeneralValidation('address1', $event)"
      @focus="onFocusGeneral('address1')"
      @blur="onBlurGeneral('address1')"
    />
    <InputWithLabel
      v-if="'address2' in inputs"
      id="address2InputArea"
      v-model="inputs.address2"
      :error-from-outer="errors.address2"
      class="inputArea"
      :class="{ isPc: !isSp }"
      area-label="番地"
      input-type="text"
      placeholder="以降の住所を入力"
      required
      :max="15"
      :validation-list="[VALIDATION_LIST.EMPTY, VALIDATION_LIST.ADDRESS]"
      @watchValidation="watchGeneralValidation('address2', $event)"
      @focus="onFocusGeneral('address2')"
      @blur="onBlurGeneral('address2')"
    />
    <InputWithLabel
      v-if="'address3' in inputs"
      id="address3InputArea"
      v-model="inputs.address3"
      :error-from-outer="errors.address3"
      class="inputArea"
      :class="{ isPc: !isSp }"
      area-label="建物名・部屋番号"
      input-type="text"
      placeholder="建物名が含まれる場合は入力"
      optional
      :max="25"
      :validation-list="[VALIDATION_LIST.ADDRESS]"
      @watchValidation="watchGeneralValidation('address3', $event)"
      @focus="onFocusGeneral('address3')"
      @blur="onBlurGeneral('address3')"
    />
    <SelectBoxArea
      id="zokuseiInputArea"
      class="inputArea"
      :class="{ isPc: !isSp }"
      area-label="学年 (高校/その他)"
      :select-list="ZOKUSEI_LIST"
      placeholder="学年を選択"
      required
      :error-from-outer="errors.zokusei"
      @change="onChangeZokusei"
      @watchValidation="watchGeneralValidation('zokusei', $event)"
    />
    <SchoolName
      v-if="'schoolName' in inputs"
      id="schoolNameInputArea"
      v-model="inputs.schoolName"
      :selected-school-code="inputsSchoolCode"
      class="inputArea"
      :class="{ isPc: !isSp }"
      :pref-code="inputs.pref"
      :error-from-outer="errors.schoolName"
      :school-name-valid-check-flg="schoolNameValidCheckFlg"
      :required="isSchoolNameRequire"
      :optional="!isSchoolNameRequire"
      :confirmed-school-value="confirmedSchoolValue"
      @handleSchoolCd="handleSchoolCd"
      @onClick="onClickSchoolNameSuggest"
      @watchValidation="watchGeneralValidation('schoolName', $event)"
      @setConfirmValue="confirmedSchoolValue = $event"
    />
    <InputWithLabel
      id="userNameInputArea"
      v-model="inputs.userName"
      :error-from-outer="errors.userName"
      class="inputArea"
      :class="{ isPc: !isSp }"
      area-label="ユーザー名"
      input-type="text"
      placeholder="ログインに使用するユーザー名を入力"
      required
      caption-text="ユーザー名は半角英数字と一部半角記号（-_.）のみ使用可能です。"
      :validation-list="[VALIDATION_LIST.EMPTY, VALIDATION_LIST.USER_NAME]"
      @watchValidation="watchUserNameValidation"
      @focus="onFocusUserName"
    />
    <PasswordDoubleCheck
      :error-from-outer-password1="errors.password.password1"
      :error-from-outer-password2="errors.password.password2"
      class="inputArea"
      :class="{ isPc: !isSp }"
      area-label="パスワード"
      input-type="password"
      placeholder="パスワードを入力"
      required
      :prop-password1="inputs.password.password1"
      :prop-password2="inputs.password.password2"
      trim-space-start-end
      :user-name="inputs.userName"
      :email-account="mailAddress.emailAccount"
      :force-validation-check-flg="forcePasswordValidationCheckFlg"
      caption-text="半角英字と半角数字の両方を組み合わせた6文字以上で入力してください。"
      @change="onChangeGeneral('password', $event)"
      @watchValidationPassword1="watchGeneralNestedValidation('password', 'password1', $event)"
      @watchValidationPassword2="watchGeneralNestedValidation('password', 'password2', $event)"
    />
    <div class="checkBoxArea" :class="{ isPc: !isSp }">
      <InputLabel v-if="!isSp" class="labelArea" :class="{ isPc: !isSp }"> お知らせ </InputLabel>
      <div>
        <CheckboxWithLabel
          ref="myCheckbox"
          v-model="isReceiveMailMagazine"
          :disabled="false"
          :class="{ isPc: !isSp }"
          label="会員用のメール配信を受け取る"
          checkbox-id="receiveMailMagazineId"
        />
        <CaptionText class="captionText">
          <ul>
            <li>進路検討に役立つ記事やイベント・入試情報などをお送りいたします。</li>
            <li>この設定は後からでも変更可能です。</li>
          </ul>
        </CaptionText>
      </div>
    </div>
    <TermsAndPolicy class="termsAndPolicyComponent" />
    <ButtonTypeCustom
      button-type="button"
      button-text="上記に同意して会員登録する"
      button-color="colorPrimary"
      button-size="sizeLarge"
      class="registerButton"
      @button-clicked="postUserData($event)"
    />
  </section>
</template>

<script>
import axios from 'axios';
import qs from 'qs';
import TitleText from './TitleText.vue';
import InputWithLabel, { VALIDATION_LIST } from '../../../common/common/Input/InputWithLabel.vue';
import InputName, { FULL_NAME_TYPE } from '../../../common/common/Input/FullName.vue';
import PasswordDoubleCheck from '../../../common/common/Input/PasswordDoubleCheck.vue';
import SelectGender from '../../../common/common/Input/Gender.vue';
import SelectYmd from '../../../common/common/Input/Ymd.vue';
import SelectBoxArea from '../../../common/common/Input/SelectBoxWithLabel.vue';
import CheckboxWithLabel from '../../../common/common/Input/CheckboxWithLabel.vue';
import CaptionText from '../../../common/common/Text/CaptionText.vue';
import InputLabel from '../../../common/common/Text/InputLabel.vue';
import PostalNumber from '../../../common/common/Input/PostalNumber.vue';
import SchoolName from '../../../common/common/Input/SchoolName.vue';
import ButtonTypeCustom from '../../../common/common/Button/ButtonTypeCustom.vue';
import TermsAndPolicy from '../../../common/common/Text/TermsAndPolicy.vue';
import { scrollToId } from '../../../../helper/_scroll';
import { GENERAL_VALIDATION_MESSAGE } from '../../../../helper/_validationCheckMessages';
import { isEmpty } from '../../../../helper/_validationCheck';
import { deepCopyObj } from '../../../../helper/_deepCopyObj';
import { initHasInnerErrorObject } from '../../../../helper/_initInputs';
import ServerErrorArea from '../../../common/common/ServerErrorArea.vue';
import {
  sendToErrorPage,
  judgeResponseErrorType,
  sendToMaintenancePage,
  ERROR_TYPES,
} from '../../../../helper/_responseError';
import { registerErrorResultMapper } from '../../../common/common/_registerResponseCheck';
import { sendLog } from '../../../../helper/_sendLog';
import { someTrue } from '../../../../helper/_objectHelper';

const EMPTY_ERROR_LIST = {
  name: {
    sei: `姓${GENERAL_VALIDATION_MESSAGE.EMPTY}`,
    mei: `名${GENERAL_VALIDATION_MESSAGE.EMPTY}`,
  },
  hiraganaName: {
    sei: `せい${GENERAL_VALIDATION_MESSAGE.EMPTY}`,
    mei: `めい${GENERAL_VALIDATION_MESSAGE.EMPTY}`,
  },
  birthday: `生年月日${GENERAL_VALIDATION_MESSAGE.NOT_SELECT}`,
  gender: `性別${GENERAL_VALIDATION_MESSAGE.NOT_SELECT}`,
  tel: `電話番号${GENERAL_VALIDATION_MESSAGE.EMPTY}`,
  postal: `郵便番号${GENERAL_VALIDATION_MESSAGE.EMPTY}`,
  pref: `都道府県${GENERAL_VALIDATION_MESSAGE.NOT_SELECT}`,
  address1: `市区町村${GENERAL_VALIDATION_MESSAGE.EMPTY}`,
  address2: `番地${GENERAL_VALIDATION_MESSAGE.EMPTY}`,
  address3: `建物名・部屋番号${GENERAL_VALIDATION_MESSAGE.EMPTY}`,
  userName: `ユーザー名${GENERAL_VALIDATION_MESSAGE.EMPTY}`,
  zokusei: `学年${GENERAL_VALIDATION_MESSAGE.NOT_SELECT}`,
  password: {
    password1: `パスワード${GENERAL_VALIDATION_MESSAGE.EMPTY}`,
    password2: `パスワード（確認）${GENERAL_VALIDATION_MESSAGE.EMPTY}`,
  },
};

// created初期化のための空データ、オーガニック向け
const ORGANIC_OBJECT_CONTENTS = {
  name: {
    sei: '',
    mei: '',
  },
  hiraganaName: {
    sei: '',
    mei: '',
  },
  birthday: '',
  gender: '',
  genderExpression: '',
  tel: '',
  userName: '',
  zokusei: '',
  password: {
    password1: '',
    password2: '',
  },
};

// created初期化のための空データ、アクション向け
const ACTION_OBJECT_CONTENTS = {
  name: {
    sei: '',
    mei: '',
  },
  hiraganaName: {
    sei: '',
    mei: '',
  },
  birthday: '',
  gender: '',
  genderExpression: '',
  tel: '',
  postal: '',
  pref: '',
  address1: '',
  address2: '',
  address3: '',
  userName: '',
  zokusei: '',
  schoolName: '',
  password: {
    password1: '',
    password2: '',
  },
};

export default {
  components: {
    TitleText,
    InputWithLabel,
    InputName,
    PasswordDoubleCheck,
    ButtonTypeCustom,
    SelectYmd,
    SelectBoxArea,
    SelectGender,
    CheckboxWithLabel,
    CaptionText,
    InputLabel,
    TermsAndPolicy,
    PostalNumber,
    SchoolName,
    ServerErrorArea,
  },
  props: {
    mailAddress: {
      type: Object,
      required: true,
    },
    prevPageType: {
      type: Object,
      required: true,
    },
    isSpFlg: {
      type: Boolean,
      required: false,
      default: undefined,
    },
    actionUserInfo: {
      type: Object,
      required: false,
      default: undefined,
    },
    isPreviousPageActionComplete: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      // ユーザー名変更時にパスワードのバリデーションチェックを強制発火させるためのフラグ
      forcePasswordValidationCheckFlg: false,
      // 住所サジェストクリック時、郵便番号の変更が1番上にemitされたことを郵便番号コンポーネントに伝えてバリデーションチェックを再発火させるためのフラグ
      postalClickFlg: false,
      // 属性変更時など学校名チェックを強制発火させたいときに
      schoolNameValidCheckFlg: false,
      // 入力内容
      inputs: {},
      // 学校選択値
      inputsSchoolCode: '',
      // コンポーネントの外部エラー
      errors: {},
      // コンポーネント内部エラーもってるかどうか
      hasInnerError: {},
      // メルマガチェック
      isReceiveMailMagazine: true,
      zokuseiList: {
        kokoseiList: [],
        zokuseiList: [],
      },
      todofukenList: [],
      postProcessingFlg: false,
      serverErrorList: [],
      errorType: undefined,
      focusFlgObj: {
        name: {
          sei: false,
          mei: false,
        },
        hiraganaName: {
          sei: false,
          mei: false,
        },
        address1: false,
        address2: false,
        address3: false,
        genderExpression: false,
      },
      postClickedFlg: false,
      // 登録した学校名・学校コードを保持する
      confirmedSchoolValue: {},
    };
  },
  computed: {
    isSp() {
      if (typeof this.isSpFlg === 'boolean') return this.isSpFlg;
      return this.$isSp;
    },
    urlPrefix() {
      return this.$isSp ? 'smp2' : 'net2';
    },
    ZOKUSEI_LIST() {
      if (this.zokuseiList.kokoseiList.length === 0) return undefined;
      return [...this.zokuseiList.kokoseiList, ...this.zokuseiList.zokuseiList];
    },
    TODOFUKEN_LIST() {
      if (this.todofukenList.length === 0) return [];
      return this.todofukenList;
    },
    VALIDATION_LIST() {
      return VALIDATION_LIST;
    },
    FULL_NAME_TYPE() {
      return FULL_NAME_TYPE;
    },
    isSchoolNameRequire() {
      if (!this.inputs.zokusei) return true;
      const kokoseiValueList = this.zokuseiList.kokoseiList.map((kokosei) => kokosei.value);
      if (kokoseiValueList.includes(this.inputs.zokusei)) return true;
      return false;
    },
    hasServerError() {
      return this.serverErrorList.length !== 0 || this.expireFlg;
    },
    isSomeFocus() {
      return someTrue(this.focusFlgObj);
    },
    actionWatchNameObjectFlg() {
      return Boolean(this.actionUserInfo.lastName) || Boolean(this.actionUserInfo.firstName);
    },
    actionWatchNameKanaObjectFlg() {
      return Boolean(this.actionUserInfo.lastNameKana) || Boolean(this.actionUserInfo.firstNameKana);
    },
  },
  watch: {
    focusFlgObj: {
      handler() {
        // すべてフォーカス中ではない、かつ「上記に同意して会員登録する」ボタンをクリックしている場合
        if (!this.isSomeFocus && this.postClickedFlg) {
          this.postUserData();
        }
      },
      deep: true,
    },
    actionUserInfo(newVal) {
      const {
        lastName,
        firstName,
        lastNameKana,
        firstNameKana,
        gender,
        genderExpression,
        zipNo1,
        zipNo2,
        todofukenCd,
        homeAddr1,
        homeAddr2,
        homeAddr3,
        homeTelNo,
      } = newVal;

      this.inputs = {
        name: {
          sei: lastName,
          mei: firstName,
        },
        hiraganaName: {
          sei: lastNameKana,
          mei: firstNameKana,
        },
        birthday: '',
        gender,
        genderExpression,
        tel: homeTelNo,
        postal: zipNo1 && zipNo2 ? zipNo1.concat(zipNo2) : '', // zipNo1,zipNo2片方無いときは不正なデータのため空文字で返す
        pref: todofukenCd,
        address1: homeAddr1,
        address2: homeAddr2,
        address3: homeAddr3,
        userName: '',
        zokusei: '',
        schoolName: '',
        password: {
          password1: '',
          password2: '',
        },
      };
      // 郵便番号のバリデーションチェックをONにするため、postalClickFlgをtrueにする
      this.postalClickFlg = true;
      this.validCheckForActionUserInfo();
    },
  },
  created() {
    this.initContents();
    this.initZokuseiList();
    if (!this.prevPageType.isOrganic() || this.isPreviousPageActionComplete) {
      this.initPrefectureList();
    }
  },
  methods: {
    onFocusGeneralNested(key, child) {
      this.focusFlgObj[key][child] = true;
    },
    onBlurGeneralNested(key, child) {
      this.focusFlgObj[key][child] = false;
    },
    onFocusGeneral(key) {
      this.focusFlgObj[key] = true;
    },
    onBlurGeneral(key) {
      this.focusFlgObj[key] = false;
    },
    initContents() {
      const initContents = this.prevPageType.isOrganic() ? ORGANIC_OBJECT_CONTENTS : ACTION_OBJECT_CONTENTS;
      this.inputs = deepCopyObj(initContents);
      this.errors = deepCopyObj(initContents);
      this.hasInnerError = initHasInnerErrorObject(deepCopyObj(initContents));
    },
    async initPrefectureList() {
      const balloonInterval = window.setInterval(() => {
        // 非同期処理直列化 ajaxFlagはグローバル変数で、非同期処理実行判定フラグとして用いている
        if (!window.ajaxFlag) return;
        window.ajaxFlag = false;

        axios
          .get(`/net2/profileItem/todofukenJson/?_=${Date.now()}`)
          .then((response) => {
            if (!response) throw Error;
            if (!response.data) throw Error;
            response.data.todofukenlist.forEach((todofuken) => {
              this.todofukenList.push({
                value: todofuken.todofukenCd,
                name: todofuken.todofukenName,
              });
            });
          })
          .catch(() => {
            // ここで取得失敗した場合、後続処理の成功はあり得ないのでエラーに飛ばす
            sendToErrorPage(this.isSp);
          })
          .finally(() => {
            window.ajaxFlag = true;
          });
        // 非同期処理直列化 ajax実行完了したら繰り返し処理を解除する
        window.clearInterval(balloonInterval);
      }, 500);
    },
    async initZokuseiList() {
      const balloonzokuInterval = window.setInterval(() => {
        // 非同期処理直列化 ajaxFlagはグローバル変数で、非同期処理実行判定フラグとして用いている
        if (!window.ajaxFlag) return;
        window.ajaxFlag = false;
        axios
          .get(`/net2/quipMember/profileItem/zokuseiJson/?_=${Date.now()}`)
          .then((response) => {
            if (!response) throw Error;
            if (!response.data) throw Error;
            response.data.kokoseiList.forEach((kokosei) => {
              this.zokuseiList.kokoseiList.push({
                value: kokosei.zokuseiCd,
                name: kokosei.zokuseiName,
              });
            });
            response.data.sonotaList.forEach((sonota) => {
              this.zokuseiList.zokuseiList.push({
                value: sonota.zokuseiCd,
                name: sonota.zokuseiName,
              });
            });
          })
          .catch(() => {
            // ここで取得失敗した場合、後続処理の成功はあり得ないのでエラーに飛ばす
            sendToErrorPage(this.isSp);
          })
          .finally(() => {
            window.ajaxFlag = true;
          });
        // 非同期処理直列化 ajax実行完了したら繰り返し処理を解除する
        window.clearInterval(balloonzokuInterval);
      }, 500);
    },
    watchGeneralValidation(key, value) {
      if (this.inputs[key] !== '') this.errors[key] = '';
      this.hasInnerError[key] = value;
    },
    watchInputTextValidation(value) {
      if (!value) this.errors.genderExpression = '';
      this.hasInnerError.genderExpression = value;
      this.errors.genderExpression = String(value);
    },
    focusGender() {
      this.focusFlgObj.genderExpression = true;
    },
    inputText(value) {
      this.inputs.genderExpression = value;
      this.focusFlgObj.genderExpression = false;
    },
    watchGeneralNestedValidation(parent, child, value) {
      if (this.inputs[parent][child] !== '') this.errors[parent][child] = '';
      this.hasInnerError[parent][child] = value;
    },
    onChangeGeneral(name, value) {
      this.inputs[name] = value;
    },
    onChangeZokusei(value) {
      this.inputs.zokusei = value;
      if (this.inputs.schoolName !== '') {
        // 学校名空でないなら再度バリデーションチェック走らせる
        this.schoolNameValidCheckFlg = !this.schoolNameValidCheckFlg;
      }
      if (this.inputs.schoolName === '' && !this.isSchoolNameRequire) {
        // 学校名空で、必須でないならバリデーションチェック走らせる
        // 必須時の入力されていませんエラー解消のため
        this.schoolNameValidCheckFlg = !this.schoolNameValidCheckFlg;
      }
    },
    handleSchoolCd(value) {
      this.inputsSchoolCode = value;
    },
    onChangePref(value) {
      this.inputs.pref = value;
    },
    onClickPostalNumberSuggest(value) {
      this.inputs.pref = value.todofukenCd;
      this.errors.pref = '';
      this.hasInnerError.pref = false;
      this.inputs.address1 = value.addr1 + value.addr2 + value.addr3;
      this.errors.address1 = '';
      this.hasInnerError.address1 = false;
      this.postalClickFlg = !this.postalClickFlg;
      this.watchGeneralValidation('pref', false);
    },
    onClickSchoolNameSuggest(value) {
      this.schoolNameValidCheckFlg = !this.schoolNameValidCheckFlg;
      this.inputsSchoolCode = value;
    },
    watchUserNameValidation(value) {
      if (this.inputs.userName !== '') this.errors.userName = '';
      // パスワード内にユーザ名を含んではいけないため、パスワードのバリデーションを強制発火させる
      this.forcePasswordValidationCheckFlg = !this.forcePasswordValidationCheckFlg;
      if (!this.errors.userName) {
        this.hasInnerError.userName = value;
      }
    },
    onFocusUserName(event) {
      sendLog(s, event.target, `${trace_p}_username_click`);
    },
    onFocusTel(event) {
      sendLog(s, event.target, `${trace_p}_tell_click`);
    },
    /**
     * ボタン押下時のバリデーションチェック
     * ボタン押下による空欄チェックと、inputコンポーネント側でエラーを持っているか？を確認する
     * エラーがあれば、1番上のエラーにスムーズスクロールで移動
     * 何か１つでもエラーあればtrue, 一切エラーがないならfalse
     */
    validCheck() {
      let errorFlg = false;
      Object.keys(this.inputs).forEach((keyName) => {
        if (keyName === 'schoolName') {
          if (this.isSchoolNameRequire) {
            // 学校名が必要な場合は最後にもう１度バリデーションチェック走らせる
            // サジェスト開いたままボタンクリックしたときのための策
            this.schoolNameValidCheckFlg = !this.schoolNameValidCheckFlg;
          }
        }
        if (typeof this.inputs[keyName] === 'object') {
          // objectの場合は中身までforEachする
          Object.keys(this.inputs[keyName]).forEach((insideKeyName) => {
            // birthdayは3つすべてが揃わないとemitしない使用上、Objectの場合は必ずバリデチェック通過するので検査しない
            if (keyName === 'birthday') return;
            if (this.hasInnerError[keyName][insideKeyName]) {
              // 内部でエラー出てるとき(object)
              if (!errorFlg) {
                scrollToId(`${keyName}InputArea`);
                errorFlg = true;
              }
            } else if (isEmpty(this.inputs[keyName][insideKeyName])) {
              // 未入力のとき（object）
              this.errors[keyName][insideKeyName] = EMPTY_ERROR_LIST[keyName][insideKeyName];
              if (!errorFlg) {
                scrollToId(`${keyName}InputArea`);
                errorFlg = true;
              }
            }
          });
        } else if (this.hasInnerError[keyName]) {
          // 内部エラー(not object)
          if (!errorFlg) {
            scrollToId(`${keyName}InputArea`);
            errorFlg = true;
          }
        } else if (isEmpty(this.inputs[keyName])) {
          if (keyName === 'schoolName' && !this.isSchoolNameRequire) return;
          if (keyName === 'address3') return;
          if (keyName === 'genderExpression') return;
          // 未入力のとき(not object)
          this.errors[keyName] = EMPTY_ERROR_LIST[keyName];
          if (!errorFlg) {
            scrollToId(`${keyName}InputArea`);
            errorFlg = true;
          }
        }
      });
      // 自由記載のエラー発生時
      if (
        !this.hasInnerError.name.mei &&
        !this.hasInnerError.name.sei &&
        !this.hasInnerError.hiraganaName.mei &&
        !this.hasInnerError.hiraganaName.sei &&
        !this.errors.birthday
      ) {
        if (this.hasInnerError.genderExpression) {
          scrollToId(`genderInputArea`);
          errorFlg = true;
        }
      }
      return errorFlg;
    },
    resetError() {
      this.errorType = undefined;
      this.serverErrorList = [];
    },
    async postUserData(event = null) {
      this.postClickedFlg = true;
      if (this.isSomeFocus) return;
      this.postClickedFlg = false;
      this.inputs.gender = this.inputs.gender === '0' ? '' : this.inputs.gender;
      if (event) {
        sendLog(s, event.target, `${trace_p}_register`);
      }
      const isHaveSomeError = this.validCheck();
      if (isHaveSomeError) return;
      if (this.postProcessingFlg) return;

      if (event) {
        const prefix = this.isSp ? 'SSignUpInput' : 'SignUpInput';
        const membermailLogSuffix = this.isReceiveMailMagazine ? 'on' : 'off';
        sendLog(s, event.target, `${prefix}_membermail_${membermailLogSuffix}`);
      }

      this.postProcessingFlg = true;

      const param =
        this.prevPageType.isOrganic() && !this.isPreviousPageActionComplete
          ? {
              userName: this.inputs.userName,
              password: this.inputs.password.password1,
              confirmationPassword: this.inputs.password.password2,
              zokuseiCd: parseInt(this.inputs.zokusei, 10),
              lastName: this.inputs.name.sei,
              firstName: this.inputs.name.mei,
              lastNameKana: this.inputs.hiraganaName.sei,
              firstNameKana: this.inputs.hiraganaName.mei,
              gender: parseInt(this.inputs.gender, 10),
              genderExpression: this.inputs.genderExpression,
              birthYear: parseInt(this.inputs.birthday.year, 10),
              birthMonth: parseInt(this.inputs.birthday.month, 10),
              birthDate: parseInt(this.inputs.birthday.day, 10),
              homeTelNo: this.inputs.tel,
              emailAccount: this.mailAddress.emailAccount,
              emailDomain: this.mailAddress.emailDomain,
              mailMagazinePermissionFlg: this.isReceiveMailMagazine === true ? '1' : '0',
              'org.apache.struts.taglib.html.TOKEN': frontend.csrfToken,
            }
          : {
              userName: this.inputs.userName,
              password: this.inputs.password.password1,
              confirmationPassword: this.inputs.password.password2,
              zokuseiCd: parseInt(this.inputs.zokusei, 10),
              lastName: this.inputs.name.sei,
              firstName: this.inputs.name.mei,
              lastNameKana: this.inputs.hiraganaName.sei,
              firstNameKana: this.inputs.hiraganaName.mei,
              gender: parseInt(this.inputs.gender, 10),
              genderExpression: this.inputs.genderExpression,
              birthYear: parseInt(this.inputs.birthday.year, 10),
              birthMonth: parseInt(this.inputs.birthday.month, 10),
              birthDate: parseInt(this.inputs.birthday.day, 10),
              homeTelNo: this.inputs.tel,
              emailAccount: this.mailAddress.emailAccount,
              emailDomain: this.mailAddress.emailDomain,
              zipNo1: this.inputs.postal.substr(0, 3),
              zipNo2: this.inputs.postal.substr(3, 4),
              todofukenCd: this.inputs.pref,
              homeAddr1: this.inputs.address1,
              homeAddr2: this.inputs.address2,
              homeAddr3: this.inputs.address3,
              schoolCd: this.inputsSchoolCode,
              mailMagazinePermissionFlg: this.isReceiveMailMagazine === true ? '1' : '0',
              'org.apache.struts.taglib.html.TOKEN': frontend.csrfToken,
            };

      axios
        .post(`/${this.urlPrefix}/quipMember/register/inputJson/`, qs.stringify(param))
        .then((response) => {
          this.resetError();
          if (!response) throw error;
          if (!response.data) throw error;
          if (response.data.resultCode === 0) {
            this.$emit('goto-next');
          } else if (response.data.resultCode === 1) {
            const mappedResponse = registerErrorResultMapper(response);
            if (typeof mappedResponse === 'object') {
              this.serverErrorList = mappedResponse;
            } else {
              this.errorType = mappedResponse;
            }
            scrollToId(`titleArea`);
          } else if (response.data.resultCode === 2) {
            sendToMaintenancePage(this.isSp);
          } else if (response.data.resultCode === 3) {
            this.$emit('goto-next', { error: true });
          } else {
            throw error;
          }
        })
        .catch((error) => {
          this.resetError();
          const judgedErrorType = judgeResponseErrorType(error);
          if (judgedErrorType === ERROR_TYPES.OTHER || judgedErrorType === ERROR_TYPES.SYSTEM_ERROR) {
            sendToErrorPage(this.isSp);
          } else {
            this.errorType = judgedErrorType;
            scrollToId(`titleArea`);
          }
        })
        .finally(() => {
          this.postProcessingFlg = false;
        });
    },
    validCheckForActionUserInfo() {
      Object.keys(this.inputs).forEach((keyName) => {
        // オートコンプリート対象外の項目のため早期リターンする
        if (
          keyName === 'birthday' ||
          keyName === 'userName' ||
          keyName === 'zokusei' ||
          keyName === 'schoolName' ||
          keyName === 'password' ||
          keyName === 'genderExpression'
        )
          return;
        // 空欄以外のバリデーションチェックは、watchGeneralValidation()とwatchGeneralNestedValidation()でチェック
        Object.keys(this.inputs[keyName]).forEach((insideKeyName) => {
          if (isEmpty(this.inputs[keyName][insideKeyName])) {
            // 未入力のとき（object）
            this.errors[keyName][insideKeyName] = EMPTY_ERROR_LIST[keyName][insideKeyName];
          }
        });
        if (isEmpty(this.inputs[keyName])) {
          if (keyName === 'address3') return;
          // 未入力のとき(not object)
          this.errors[keyName] = EMPTY_ERROR_LIST[keyName];
        }
      });
      // 既存実装に影響がないように値を戻す
      this.postalClickFlg = false;
    },
    changeInputValue(value) {
      this.inputs.genderExpression = value;
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'css/contents/common/global';

.inputUserDataSection {
  padding: 24px 20px 40px;

  &.isPc {
    padding: 0;
  }

  .inputArea {
    margin-top: 20px;

    &.isPc {
      margin-top: 24px;
    }
  }

  .checkBoxArea {
    display: grid;
    grid-row-gap: 20px;
    margin-top: 24px;

    &.isPc {
      grid-template-columns: 184px 1fr;
    }

    .labelArea {
      &.isPc {
        grid-row: 1/3;
        width: 184px;
      }
    }
  }

  .termsAndPolicyComponent {
    margin-top: 32px;

    &.isPc {
      margin-top: 32px;
    }
  }

  .registerButton {
    margin-top: 12px;
  }

  .serverErrorArea {
    margin-bottom: 24px;
  }
}
</style>
